/**
 * /dashboard/accounting/journals/new — post a manual journal entry
 * (Sprint 4 §C).
 *
 * A dynamic lines grid (>= 2 rows) where each line is a single amount
 * with a Dr/Cr selector (debit XOR credit), an account picker (reuses
 * the chart-of-accounts list the document line editor uses), an optional
 * description and an optional contact. Live running Debit / Credit
 * totals + a balanced indicator gate the submit button: a posting must
 * balance (debits == credits > 0) and have at least 2 lines.
 *
 * On submit: POST /accounting/journal-entries. period_locked surfaces on
 * the date field; everything else toasts. On 201 we land on the Trial
 * Balance so the operator can see the posting reflected immediately.
 *
 * Role-gate: accountingRole = company_admin | hr_manager | accountant.
 * Feature-gate: RequireFeature("accounting") — enforced by the backend;
 * the page is wrapped in <AccountingFeatureGate>.
 */

import { useMemo, useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import { todayISO } from "@/lib/leave-format";
import { useAccounts, useContacts, useCreateManualJournal } from "@/hooks/use-accounting";
import {
  ACCOUNT_TYPE_LABELS,
  type AccountType,
  type CreateManualJournalLineRequest,
} from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { AccountingFeatureGate } from "@/components/accounting/accounting-feature-gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/journals/new",
)({
  component: NewJournalRoute,
});

// ---------------------------------------------------------------------------
// Editable line state. Amount is a rupee STRING (round-trips
// rupeesToPaise); `side` selects whether that amount is a debit or a
// credit. account_id / contact_id are optional numbers (0 = unset).
// ---------------------------------------------------------------------------

type Side = "debit" | "credit";

interface EditableJournalLine {
  _localId: string;
  account_id?: number;
  side: Side;
  amount_rupees: string;
  description: string;
  contact_id?: number;
}

const NO_CONTACT = "__none__";

function blankLine(side: Side = "debit"): EditableJournalLine {
  return {
    _localId: crypto.randomUUID(),
    side,
    amount_rupees: "",
    description: "",
  };
}

/** Parsed paise for a line, or 0 when blank/invalid (UI-only preview). */
function linePaise(l: EditableJournalLine): number {
  return rupeesToPaise(l.amount_rupees) ?? 0;
}

function NewJournalRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const navigate = useNavigate();

  const accountsQ = useAccounts();
  const contactsQ = useContacts({ include_archived: false });
  const createMut = useCreateManualJournal();

  const accounts = useMemo(
    () =>
      [...(accountsQ.data?.items ?? [])].sort((a, b) =>
        a.code.localeCompare(b.code),
      ),
    [accountsQ.data],
  );
  const contacts = useMemo(
    () =>
      [...(contactsQ.data?.items ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [contactsQ.data],
  );

  const [date, setDate] = useState<string>(todayISO());
  const [dateError, setDateError] = useState<string | null>(null);
  const [narration, setNarration] = useState<string>("");
  const [lines, setLines] = useState<EditableJournalLine[]>([
    blankLine("debit"),
    blankLine("credit"),
  ]);

  // Running totals (paise).
  const totalDebit = lines
    .filter((l) => l.side === "debit")
    .reduce((sum, l) => sum + linePaise(l), 0);
  const totalCredit = lines
    .filter((l) => l.side === "credit")
    .reduce((sum, l) => sum + linePaise(l), 0);
  const diff = totalDebit - totalCredit;
  const balanced = diff === 0 && totalDebit > 0;

  // Submit gate: balanced + every line has an account + a positive amount.
  const allLinesValid = lines.every(
    (l) => l.account_id != null && l.account_id > 0 && linePaise(l) > 0,
  );
  const canSubmit =
    balanced &&
    lines.length >= 2 &&
    narration.trim() !== "" &&
    allLinesValid &&
    !createMut.isPending;

  function updateLine(id: string, patch: Partial<EditableJournalLine>) {
    setLines((prev) =>
      prev.map((l) => (l._localId === id ? { ...l, ...patch } : l)),
    );
  }

  function addLine() {
    // Seed the new row to the side that's currently short, so the
    // operator nudges towards balance.
    setLines((prev) => [
      ...prev,
      blankLine(diff > 0 ? "credit" : "debit"),
    ]);
  }

  function removeLine(id: string) {
    setLines((prev) => {
      const next = prev.filter((l) => l._localId !== id);
      // Never drop below 2 rows.
      return next.length >= 2 ? next : prev;
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setDateError(null);

    const trimmedNarration = narration.trim();
    if (trimmedNarration === "") {
      toast.error("Narration is required.");
      return;
    }
    if (trimmedNarration.length > 500) {
      toast.error("Narration must be 500 characters or fewer.");
      return;
    }

    const wireLines: CreateManualJournalLineRequest[] = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (l.account_id == null || l.account_id <= 0) {
        toast.error(`Line ${i + 1}: pick an account.`);
        return;
      }
      const paise = rupeesToPaise(l.amount_rupees);
      if (paise == null || paise <= 0) {
        toast.error(`Line ${i + 1}: enter an amount greater than zero.`);
        return;
      }
      const wire: CreateManualJournalLineRequest = { account_id: l.account_id };
      if (l.side === "debit") wire.debit_minor = paise;
      else wire.credit_minor = paise;
      if (l.description.trim() !== "")
        wire.description = l.description.trim();
      if (l.contact_id != null && l.contact_id > 0)
        wire.contact_id = l.contact_id;
      wireLines.push(wire);
    }

    if (!balanced) {
      toast.error("Debits and credits must balance before posting.");
      return;
    }

    try {
      const out = await createMut.mutateAsync({
        date,
        narration: trimmedNarration,
        lines: wireLines,
      });
      toast.success(`Journal ${out.entry_no} posted.`);
      void navigate({
        to: "/app/$tenantSlug/accounting/reports/trial-balance",
        params: { tenantSlug },
      });
    } catch (err) {
      const apiErr = toApiError(err);
      if (apiErr.field === "date") {
        setDateError(apiErr.message);
        return;
      }
      toast.error(apiErr.message);
    }
  }

  return (
    <AccountingFeatureGate>
      <AccountingModuleShell
        title="New Journal Entry"
        description="Hand-key a balanced double-entry journal. Debits must equal credits (Companies Act §128(1))."
        actions={
          <Button asChild variant="outline" size="sm" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/journals"
              params={{ tenantSlug }}
            >
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back to Journals
            </Link>
          </Button>
        }
      >
        <form onSubmit={onSubmit} className="space-y-5">
          {/* Header fields */}
          <Card>
            <CardContent className="grid gap-4 py-4 sm:grid-cols-[12rem_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="je-date">Date *</Label>
                <Input
                  id="je-date"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    setDateError(null);
                  }}
                  required
                  aria-invalid={dateError ? true : undefined}
                  className={dateError ? "border-destructive" : undefined}
                />
                {dateError && (
                  <p className="text-xs text-destructive">{dateError}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="je-narration">Narration *</Label>
                <Input
                  id="je-narration"
                  value={narration}
                  maxLength={500}
                  onChange={(e) => setNarration(e.target.value)}
                  placeholder="What this entry records"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {narration.trim().length}/500
                </p>
              </div>
            </CardContent>
          </Card>

          {accountsQ.isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load accounts</AlertTitle>
              <AlertDescription>
                {toApiError(accountsQ.error).message}
              </AlertDescription>
            </Alert>
          )}

          {/* Lines */}
          <Card>
            <CardContent className="space-y-3 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Lines</h3>
                <span className="text-xs text-muted-foreground">
                  At least 2 lines; each line is a debit or a credit.
                </span>
              </div>

              <div className="rounded-md border">
                {/* Desktop header */}
                <div className="hidden grid-cols-[1.6fr_0.8fr_1fr_1.6fr_1.4fr_auto] gap-2 border-b bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid">
                  <span>Account</span>
                  <span>Dr/Cr</span>
                  <span>Amount</span>
                  <span>Description</span>
                  <span>Contact</span>
                  <span aria-hidden />
                </div>

                {lines.map((line, i) => (
                  <div
                    key={line._localId}
                    data-testid={`je-line-${i}`}
                    className="grid grid-cols-1 gap-2 border-b px-3 py-3 last:border-b-0 sm:grid-cols-[1.6fr_0.8fr_1fr_1.6fr_1.4fr_auto] sm:items-center sm:py-2"
                  >
                    {/* Account */}
                    <div className="space-y-1">
                      <Label className="text-xs sm:hidden">Account</Label>
                      <Select
                        value={
                          line.account_id != null
                            ? String(line.account_id)
                            : ""
                        }
                        onValueChange={(v) =>
                          updateLine(line._localId, {
                            account_id: v ? Number(v) : undefined,
                          })
                        }
                        disabled={accountsQ.isLoading}
                      >
                        <SelectTrigger
                          aria-label={`Account for line ${i + 1}`}
                          className={
                            line.account_id == null
                              ? "border-red-300"
                              : undefined
                          }
                        >
                          <SelectValue placeholder="Pick an account" />
                        </SelectTrigger>
                        <SelectContent>
                          {accounts.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.code} — {a.name}
                              <span className="ml-1 text-muted-foreground">
                                ({ACCOUNT_TYPE_LABELS[a.type as AccountType]})
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dr/Cr */}
                    <div className="space-y-1">
                      <Label className="text-xs sm:hidden">Dr / Cr</Label>
                      <Select
                        value={line.side}
                        onValueChange={(v) =>
                          updateLine(line._localId, { side: v as Side })
                        }
                      >
                        <SelectTrigger
                          aria-label={`Debit or credit for line ${i + 1}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debit">Dr</SelectItem>
                          <SelectItem value="credit">Cr</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Amount */}
                    <div className="space-y-1">
                      <Label className="text-xs sm:hidden">Amount (₹)</Label>
                      <Input
                        aria-label={`Amount for line ${i + 1}`}
                        inputMode="decimal"
                        value={line.amount_rupees}
                        onChange={(e) =>
                          updateLine(line._localId, {
                            amount_rupees: e.target.value,
                          })
                        }
                        placeholder="0.00"
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                      <Label className="text-xs sm:hidden">Description</Label>
                      <Input
                        aria-label={`Description for line ${i + 1}`}
                        value={line.description}
                        onChange={(e) =>
                          updateLine(line._localId, {
                            description: e.target.value,
                          })
                        }
                        placeholder="Optional"
                      />
                    </div>

                    {/* Contact */}
                    <div className="space-y-1">
                      <Label className="text-xs sm:hidden">Contact</Label>
                      <Select
                        value={
                          line.contact_id != null
                            ? String(line.contact_id)
                            : NO_CONTACT
                        }
                        onValueChange={(v) =>
                          updateLine(line._localId, {
                            contact_id:
                              v === NO_CONTACT ? undefined : Number(v),
                          })
                        }
                        disabled={contactsQ.isLoading}
                      >
                        <SelectTrigger
                          aria-label={`Contact for line ${i + 1}`}
                        >
                          <SelectValue placeholder="— (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_CONTACT}>—</SelectItem>
                          {contacts.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Remove */}
                    <div className="flex items-center justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(line._localId)}
                        aria-label={`Remove line ${i + 1}`}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        disabled={lines.length <= 2}
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLine}
                className="gap-1.5"
              >
                <Plus aria-hidden className="h-4 w-4" />
                Add Line
              </Button>
            </CardContent>
          </Card>

          {/* Totals + balance indicator */}
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Total debit
                  </p>
                  <p className="text-base font-semibold tabular-nums">
                    {formatPaise(totalDebit)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Total credit
                  </p>
                  <p className="text-base font-semibold tabular-nums">
                    {formatPaise(totalCredit)}
                  </p>
                </div>
              </div>
              <div
                data-testid="je-balance-indicator"
                className={
                  balanced
                    ? "rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700"
                    : "rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700"
                }
              >
                {balanced
                  ? "Balanced ✓"
                  : totalDebit === 0 && totalCredit === 0
                    ? "Enter amounts to balance"
                    : `Out of balance by ${formatPaise(Math.abs(diff))}`}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button asChild type="button" variant="outline">
              <Link
                to="/app/$tenantSlug/accounting/journals"
                params={{ tenantSlug }}
              >
                Cancel
              </Link>
            </Button>
            <Button type="submit" disabled={!canSubmit} className="gap-1.5">
              {createMut.isPending && (
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              )}
              Post Journal
            </Button>
          </div>
        </form>
      </AccountingModuleShell>
    </AccountingFeatureGate>
  );
}
